import { useState, useCallback } from 'react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import type { CharacterSheet, SheetTemplate } from '@roll1/shared';
import { useSheetContext } from '../../contexts/SheetContext';
import { useI18n } from '../../i18n';

interface PublicFieldsEditorProps {
  sheet: CharacterSheet;
  template: SheetTemplate;
}

/**
 * Editor for sheet public fields (which fields are visible to non-owners).
 * GM-only. Sends sheet:set-public-fields via onSendSheet.
 */
export default function PublicFieldsEditor({ sheet, template }: PublicFieldsEditorProps) {
  const { onSendSheet } = useSheetContext();
  const [publicFields, setPublicFields] = useState<Set<string>>(new Set(sheet.publicFields));

  const toggleField = useCallback((key: string) => {
    setPublicFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);

      // Send immediately
      onSendSheet({
        type: 'sheet:set-public-fields',
        sheetId: sheet.id,
        publicFields: Array.from(next),
      });

      return next;
    });
  }, [sheet.id, onSendSheet]);

  // Collect all fields from template
  const allFields = template.sections.flatMap(s =>
    s.fields.map(f => ({ key: f.key, label: f.label, section: s.label }))
  );

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-ui font-medium">Visible Fields</Label>
      <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
        {allFields.map(f => (
          <label key={f.key} className="flex items-center gap-2 text-meta cursor-pointer">
            <Checkbox
              checked={publicFields.has(f.key)}
              onCheckedChange={() => toggleField(f.key)}
            />
            <span>{f.label} <span className="text-muted-foreground">({f.section})</span></span>
          </label>
        ))}
      </div>
    </div>
  );
}
